import { FC, memo, useCallback, useMemo } from 'react';
import { useController } from 'react-hook-form';

import FormControl from '@material-ui/core/FormControl';
import moment from 'moment';

import { USADateTimeFormat } from '../../utils';
import { HelperTxt } from '../helperTxt/HelperTxt';
import { ClockTimepicker } from '../pickers';
import { FormClockTimepickerProps } from './FormItems.types';

export const FormClockTimepicker: FC<FormClockTimepickerProps> = memo(
  ({ control, name, required, valueFormat, ignoreError, className, ...rest }) => {
    const {
      field: { value, onChange },
      fieldState: { error },
    } = useController({
      name,
      control,
      defaultValue: undefined,
    });

    const isError = useMemo(() => !ignoreError && !!error, [error, ignoreError]);

    const helperText = useMemo(() => {
      if (!ignoreError && error?.message) {
        return error.message;
      }

      if (required) {
        return '*required';
      }

      return '';
    }, [error?.message, ignoreError, required]);

    const handleConfirm = useCallback(
      (time: string | moment.Moment) => {
        const formattedTime = moment(time).format(valueFormat ?? USADateTimeFormat);
        onChange(formattedTime);
      },
      [valueFormat, onChange],
    );

    return (
      <FormControl error={isError} fullWidth className={className}>
        <ClockTimepicker {...rest} value={value} error={isError} handleConfirm={handleConfirm} />

        {helperText && <HelperTxt>{helperText}</HelperTxt>}
      </FormControl>
    );
  },
);
